import { Injectable } from '@angular/core';
import { convertMbToBytes, flipObject, latinizeStr } from '@bazis/utils';
import { IsActiveMatchOptions } from '@angular/router';
import { MaskSettings } from '@bazis/form/models/form-element.types';
import { BazisConfigurationService } from '@bazis/configuration.service';
import * as bazisConfig from '@bazis/configuration.service';

export const AVAILABLE_LANGUAGES = ['ru'];
export const SELECTED_LANGUAGE =
    AVAILABLE_LANGUAGES.indexOf(localStorage.getItem('lang')) > -1
        ? localStorage.getItem('lang')
        : 'ru';

export const API_DATETIME_FORMAT = 'YYYY-MM-DD[T]HH:mm:ssZ';
export const API_DATETIME_FORMAT_Z = 'YYYY-MM-DD[T]HH:mm:ss[Z]';

const VAT = 20;

export const SUCCESS_INVITE_PAGE = '/success-invite';

export const MAX_FILE_SIZE = convertMbToBytes(10); // 10мб

export const TG_BOT = '@smartwaste_bot';
export const TG_COMMAND = '/start';

export const DOCUMENT_LINKS = {
    privacyPolicy: '/legal-documents/privacy_policy',
    userAgreement: '/legal-documents/site_rules',
    cookiePolicy: '/legal-documents/cookie_policy',
    agencyContract: '/legal-documents/agency_contract',
    partnerContract: '/legal-documents/partner_contract',
    buyerContract: '/legal-documents/sales_agency_contract',
};

export const UNDERCONSTRUCTION_TITLE = {
    feature: 'title.featureUnderconstruction',
    section: 'title.sectionUnderconstruction',
};

export const DATETIME_LOCALE = bazisConfig.DATETIME_LOCALE;

export const ROLE = {
    destroyer: 'LK_CONTRACTOR',
    organizator: 'LK_SPONSOR',
    landfill: 'LK_RECYCLER',
    landfillDirector: 'LK_RECYCLER_DIRECTOR',
    landfillLawyer: 'LK_RECYCLER_LAWYER',
    transporter: 'LK_CARRIER',
    systemOperator: 'LK_ADMIN',
    builder: 'LK_BUILDER',
    uoosp: 'LK_UOOSP',
    planner: 'LK_PLANNER',
    holding: 'LK_HOLDING',
    // районный акимат — заказчик услуг приёма смета и КГО (ТЗ «Разделение учёта», п. 5)
    akimat: 'LK_AKIMAT',

    driver: 'LK_DRIVER',
    wasteAcceptor: 'LK_ACCEPTOR',
    foreman: 'LK_FOREMAN',
    foremanBuilder: 'LK_FOREMAN_BUILDER',

    anonymous: 'LK_PUBLIC_USER',
    empty: null,
};

export const ROLE_ACCOUNT_TYPE = {};

export const DISABLE_NOTIFICATIONS_ROLES = [
    ROLE.anonymous,
    ROLE.driver,
    ROLE.wasteAcceptor,
    ROLE.foreman,
    ROLE.foremanBuilder,
];

export const COLORS = {
    primary: '#00A2AD',
    secondary: '#43B1F2',
    tertiary: '#817AF9',
    action: '#164982',
    info: '#C4C4C4',
    success: '#78C649',
    warning: '#F6BF36',
    danger: '#ED1A34',
    light: '#f4f5f8',
    medium: '#92949c',
    dark: '#111214',
};

export const AUTH_PAGE_URL = '/login';

export const ALL_SEARCH_MODELS = [
    {
        id: 'all',
        nameKey: 'search.all',
    },
    {
        id: 'fulfilment.carriertask',
        nameKey: 'search.tasks',
        urlStart: '/carrier-tasks/',
    },
    {
        id: 'carrier.vehicle',
        nameKey: 'search.vehicle',
        urlStart: '/vehicles/',
    },
];

export const ROLE_SEARCH_MODEL_IDS = {
    [ROLE.destroyer]: ['all', 'fulfilment.carriertask'],
    [ROLE.landfill]: ['all', 'facility.facility'],
    [ROLE.landfillDirector]: ['all', 'facility.facility'],
    [ROLE.landfillLawyer]: ['all', 'facility.facility'],
    [ROLE.transporter]: ['all', 'carrier.vehicle'],
    [ROLE.systemOperator]: ALL_SEARCH_MODELS.map((v) => v.id),
    [ROLE.anonymous]: [],
    [ROLE.driver]: [],
    [ROLE.wasteAcceptor]: [],
    [ROLE.foreman]: [],
    [ROLE.foremanBuilder]: [],
    [ROLE.empty]: [],
};

export const ROLE_REVERT = flipObject(ROLE);

export const CARRIER_REPORTS_PRIORITY = {
    created: {
        id: 'created',
        titleKey: 'created',
        dispatcherStatus: 'created',
        color: 'secondary',
        reportType: '',
        priority: 0,
    },
    start_reports: {
        id: 'start_reports',
        reportType: 'fulfilment.start_report',
        titleKey: 'start',
        color: 'primary',
        priority: 1,
    },
    facility_arrival_reports: {
        id: 'facility_arrival_reports',
        reportType: 'fulfilment.facility_arrival_report',
        titleKey: 'arrivedAtFacility',
        color: 'primary',
        priority: 2,
    },
    unloading_reports: {
        id: 'unloading_reports',
        reportType: 'fulfilment.unloading_report',
        titleKey: 'completedUnloading',
        color: 'primary',
        priority: 3,
    },
    completed_task_reports: {
        id: 'completed_task_reports',
        reportType: 'fulfilment.completed_task_report',
        titleKey: 'acceptedByFacility',
        color: 'success',
        priority: 4,
    },
    incompleted_task_reports: {
        id: 'incompleted_task_reports',
        reportType: 'fulfilment.incompleted_task_report',
        titleKey: 'notAcceptedByFacility',
        color: 'danger',
        priority: 4,
    },
    impossible_task_reports: {
        id: 'impossible_task_reports',
        reportType: 'fulfilment.impossible_task_report',
        titleKey: 'impossible',
        dispatcherStatus: 'error',
        color: 'danger',
        priority: 10,
    },
};

export const REPORT_TYPES = Object.values(CARRIER_REPORTS_PRIORITY)
    .map((v) => v.reportType)
    .filter((v) => !!v);

export const SHARE_REPLAY_SETTINGS = { bufferSize: 1, refCount: true };

export const PHONE_MASK = bazisConfig.PHONE_MASK;
export const PHONE_PATTERN = bazisConfig.PHONE_PATTERN;

export const VEHICLE_MASK_SETTINGS: Partial<MaskSettings> = {
    mask: 'VVVVVVVVVV',
    patterns: {
        V: {
            pattern: new RegExp('^[1234567890a-zавекмнорстухA-ZАВЕКМНОРСТУХ]'),
            optional: true,
        },
    },
};

export const GNUM_PATTERN = /^([a-zA-Z]{0,2}\d{3}[a-zA-Z]{2,3}\d{0,2}|[a-zA-Z]\d{4}\d{2})$/;
export const PHONE_MASK_SETTINGS: Partial<MaskSettings> = bazisConfig.PHONE_MASK_SETTINGS;

export const PHONE_INPUT_TRANSFORM_FN = bazisConfig.PHONE_INPUT_TRANSFORM_FN;

export const COMMON_DATE_MASK_SETTINGS = bazisConfig.COMMON_DATE_MASK_SETTINGS;

export const SNILS_PATTERN = bazisConfig.SNILS_PATTERN;
export const SNILS_MASK_SETTINGS = bazisConfig.SNILS_MASK_SETTINGS;

export const BIC_PATTERN = /[0-9a-zA-Z]/;
export const RRC_PATTERN = bazisConfig.RRC_PATTERN;
export const INN_PATTERN = bazisConfig.INN_PATTERN;
export const BANK_ACCOUNT_PATTERN = /^[0-9a-zA-Z]{20}$/;
export const CORRESPONDENT_ACCOUNT_PATTERN = bazisConfig.CORRESPONDENT_ACCOUNT_PATTERN;
export const OKTMO_MASK_SETTINGS = bazisConfig.OKTMO_MASK_SETTINGS;

export const FKKO_UNIVERSAL_FILTER = { class_fkko: [3, 4, 5] };
export const AVG_SPEED = 50;

export const MOBILE_ROLES = [ROLE.driver, ROLE.wasteAcceptor, ROLE.foreman, ROLE.foremanBuilder];

export const HEADER_MENU = {
    [ROLE.anonymous]: [
        {
            titleKey: 'header.infoCenter',
            url: '/',
            children: [
                {
                    titleKey: 'header.agreement',
                    url: DOCUMENT_LINKS.userAgreement,
                },
                {
                    titleKey: 'header.faq',
                    url: '/faq',
                },
            ],
        },
    ],
    [ROLE.driver]: [],
    [ROLE.foreman]: [],
    [ROLE.wasteAcceptor]: [],
    [ROLE.foremanBuilder]: [],
    [ROLE.destroyer]: [
        {
            titleKey: 'header.objects',
            url: '/objects',
        },
        {
            titleKey: 'header.cleanup',
            url: '/cleanup',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        {
            titleKey: 'header.acts',
            url: '/acts',
        },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
        {
            titleKey: 'header.analytics',
            url: '/analytics',
        },
        {
            titleKey: 'header.vehicles',
            url: '/vehicles',
            children: [
                {
                    titleKey: 'header.myVehicleList',
                    url: '/vehicles',
                },
                {
                    titleKey: 'header.setupTelemetry',
                    url: '/setup-telemetry',
                },
            ],
        },
    ],
    // районный акимат: свои услуги приёма смета и КГО и рейсы по ним
    [ROLE.akimat]: [
        {
            titleKey: 'header.cleanup',
            url: '/cleanup',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
    ],
    [ROLE.planner]: [
        {
            titleKey: 'header.objects',
            url: '/objects',
        },
        {
            titleKey: 'header.contracts',
            url: '/contracts',
        },
        {
            titleKey: 'header.buildingObjects',
            url: '/building-objects',
        },
        {
            titleKey: 'header.digitalMap',
            url: '/digital-map',
        },
    ],
    [ROLE.builder]: [
        {
            titleKey: 'header.contracts',
            url: '/contracts',
        },
        {
            titleKey: 'header.buildingObjects',
            url: '/building-objects',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
        {
            titleKey: 'header.acts',
            url: '/acts',
        },
        {
            titleKey: 'header.analytics',
            url: '/analytics',
        },
        {
            titleKey: 'header.vehicles',
            url: '/vehicles',
            children: [
                {
                    titleKey: 'header.myVehicleList',
                    url: '/vehicles',
                },
                {
                    titleKey: 'header.setupTelemetry',
                    url: '/setup-telemetry',
                },
            ],
        },
    ],
    [ROLE.holding]: [
        {
            titleKey: 'header.contracts',
            url: '/contracts',
        },
        {
            titleKey: 'header.buildingObjects',
            url: '/building-objects',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
        {
            titleKey: 'header.acts',
            url: '/acts',
        },
        {
            titleKey: 'header.analytics',
            url: '/analytics',
        },
    ],
    [ROLE.uoosp]: [
        {
            titleKey: 'header.analytics',
            url: '/analytics',
        },
        {
            titleKey: 'header.contracts',
            url: '/contracts',
        },
        {
            titleKey: 'header.buildingObjects',
            url: '/building-objects',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        {
            titleKey: 'header.digitalMap',
            url: '/digital-map',
        },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
        {
            titleKey: 'header.agreements',
            url: '/agreements',
        },
    ],
    [ROLE.organizator]: [
        {
            titleKey: 'header.objects',
            url: '/objects',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        {
            titleKey: 'header.digitalMap',
            url: '/digital-map',
        },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
    ],
    [ROLE.landfill]: [
        {
            titleKey: 'header.landfills',
            url: '/landfills',
        },
        {
            titleKey: 'header.visitFacts',
            url: '/visit-facts',
        },
        {
            titleKey: 'header.demolitionContracts',
            url: '/objects',
        },
        {
            titleKey: 'header.constructionContracts',
            url: '/contracts',
        },
        {
            titleKey: 'header.buildingObjects',
            url: '/building-objects',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        // {
        //     titleKey: 'header.analytics',
        //     url: '/analytics',
        // },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
        {
            titleKey: 'header.acts',
            url: '/acts',
        },
    ],
    [ROLE.landfillDirector]: [
        {
            titleKey: 'header.landfills',
            url: '/landfills',
        },
        {
            titleKey: 'header.visitFacts',
            url: '/visit-facts',
        },
        {
            titleKey: 'header.demolitionContracts',
            url: '/objects',
        },
        {
            titleKey: 'header.constructionContracts',
            url: '/contracts',
        },
        {
            titleKey: 'header.buildingObjects',
            url: '/building-objects',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        // {
        //     titleKey: 'header.analytics',
        //     url: '/analytics',
        // },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
        {
            titleKey: 'header.acts',
            url: '/acts',
        },
    ],
    [ROLE.landfillLawyer]: [
        {
            titleKey: 'header.landfills',
            url: '/landfills',
        },
        {
            titleKey: 'header.visitFacts',
            url: '/visit-facts',
        },
        {
            titleKey: 'header.demolitionContracts',
            url: '/objects',
        },
        {
            titleKey: 'header.constructionContracts',
            url: '/contracts',
        },
        {
            titleKey: 'header.buildingObjects',
            url: '/building-objects',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        // {
        //     titleKey: 'header.analytics',
        //     url: '/analytics',
        // },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
        {
            titleKey: 'header.acts',
            url: '/acts',
        },
    ],
    [ROLE.transporter]: [
        {
            titleKey: 'header.vehicles',
            url: '/vehicles',
            children: [
                {
                    titleKey: 'header.myVehicleList',
                    url: '/vehicles',
                },
                {
                    titleKey: 'header.setupTelemetry',
                    url: '/setup-telemetry',
                },
            ],
        },

        {
            titleKey: 'header.objects',
            url: '/bids',
        },

        {
            titleKey: 'header.demolitionContracts',
            url: '/objects',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        // {
        //     titleKey: 'header.analytics',
        //     url: '/analytics',
        // },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
        {
            titleKey: 'header.acts',
            url: '/acts',
        },
        {
            titleKey: 'header.analytics',
            url: '/analytics',
        },
    ],
    [ROLE.systemOperator]: [
        {
            titleKey: 'header.digitalMap',
            url: '/digital-map',
        },
        {
            titleKey: 'header.analytics',
            url: '/analytics',
        },
        {
            titleKey: 'header.carrierTasks',
            url: '/carrier-tasks',
        },
        {
            titleKey: 'header.landfills',
            url: '/landfills',
        },
        {
            titleKey: 'header.visitFacts',
            url: '/visit-facts',
        },
        {
            titleKey: 'header.companyUsers',
            url: '/users',
        },
        {
            titleKey: 'header.objects',
            url: '/objects',
        },
        {
            titleKey: 'header.cleanup',
            url: '/cleanup',
        },
        {
            titleKey: 'header.contracts',
            url: '/contracts',
        },
        {
            titleKey: 'header.buildingObjects',
            url: '/building-objects',
        },
        {
            titleKey: 'header.more',
            url: '/',
            type: 'more',
            children: [
                {
                    titleKey: 'header.organizations',
                    url: '/organizations',
                },
                {
                    titleKey: 'header.vehicles',
                    url: '/vehicles',
                },
                {
                    titleKey: 'header.agencyAgreements',
                    url: '/agency-agreements',
                },
                {
                    titleKey: 'header.acts',
                    url: '/acts',
                },
                {
                    titleKey: 'header.setupTelemetry',
                    url: '/setup-telemetry',
                },
                {
                    titleKey: 'header.agreements',
                    url: '/agreements',
                },
            ],
        },
    ],
    [ROLE.empty]: [{}],
};

export const USER_MENU = [
    {
        titleKey: 'header.personalInfo',
        url: '/profile/personal-info',
        icon: 'portrait',
        excludeForRoles: [],
    },
    {
        titleKey: 'header.notifications',
        url: '/profile/notifications',
        icon: 'bell',
        excludeForRoles: [],
    },
    {
        type: 'roles',
        titleKey: 'header.enterAs',
        icon: 'arrow-right',
        excludeForRoles: [],
    },
    {
        titleKey: 'header.logout',
        url: '/logout',
        icon: 'power',
        excludeForRoles: [],
    },
];

export const USER_STATUSES = {
    active: {
        color: 'success',
        nameKey: 'users.status.active',
    },
    waiting: {
        color: 'secondary',
        nameKey: 'users.status.waiting',
    },
    blocked: {
        color: 'danger',
        nameKey: 'users.status.blocked',
    },
};

export const FOOTER_MENU = {
    [ROLE.destroyer]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.organizator]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.landfill]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.landfillDirector]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.landfillLawyer]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.transporter]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.systemOperator]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.builder]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.uoosp]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.planner]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
    [ROLE.holding]: [
        {
            titleKey: 'footer.profileSettings',
            url: '/profile/settings',
        },
    ],
};

export const DEFAULT_LINK_ACTIVE_MATCH_OPTIONS: IsActiveMatchOptions =
    bazisConfig.DEFAULT_LINK_ACTIVE_MATCH_OPTIONS;

export const SW_ORG_TIN = '7707459899';

export const KBE_MASK_SETTINGS: Partial<MaskSettings> = {
    mask: 'FS',
    patterns: {
        F: { pattern: new RegExp(/[1-2]/) },
        S: { pattern: new RegExp(/[1-9]/) },
    },
};

export const DEFAULT_POLYGON_SETTINGS = {
    stroke: true,
    weight: 2,
    color: '#00A2AD',
    fill: true,
    fillColor: '#00A2AD',
    fillOpacity: 0.1,
};

@Injectable({
    providedIn: 'root',
})
export class ConfigurationService extends BazisConfigurationService {
    protected configuration: any = {
        tiles: {
            default: {
                url: 'https://{s}.map.asu.big3.ru/street/{z}/{x}/{y}.png',
                size: 256,
            },
            grey: {
                url: 'https://api.mapbox.com/styles/v1/smart-waste/cl7t82ptn006315lg5yiys80b/tiles/256/{z}/{x}/{y}?&access_token=pk.eyJ1Ijoic21hcnQtd2FzdGUiLCJhIjoiY2w3dDdyYWtvMDFzcjNucWxkaDR6aXk3dSJ9.DwsYQbL40ED-guYA9sE-0g',
                size: 256,
            },
        },
        emptyInputValue: '-',
        defaultLocation: [51.143964, 71.435819],
        contacts: { email: 'info@smartwaste.ru', phone: '+7 495 822-22-33' },
        vehicleNumberMask: ['VV', '00000'],
    };

    protected settingsUrl = 'project_settings/settings';

    selectedLanguage = SELECTED_LANGUAGE;

    availableLanguages = AVAILABLE_LANGUAGES;

    readonly bazisApiV: '1' | '2' = '2';

    get isNominalAccountAvailable() {
        return false;
    }

    get contacts() {
        return this.configuration.PLATFORM_PHONE ||
            this.configuration.PLATFORM_EMAIL ||
            this.configuration.PLATFORM_TG
            ? {
                  phone: this.configuration.PLATFORM_PHONE,
                  email: this.configuration.PLATFORM_EMAIL,
                  tg: this.configuration.PLATFORM_TG,
              }
            : null;
    }

    get appAndroidLink(): any {
        return this.configuration?.APP_ANDROID_URL || '';
    }

    get appIOsLink(): any {
        return this.configuration?.APP_IOS_URL || '';
    }

    get vat(): any {
        return this.configuration?.SW_PAYMENTS_VAT || VAT;
    }

    get signatureType(): 'NCALayer' {
        return 'NCALayer';
    }
}
